import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { GameMode } from '../../types';
import { UserProfile } from '../../types/profile';
import { gameModeConfigs } from '../../data/gameObjects';
import { getCategoryInfo, getAvailableCategories } from '../../data/objects';
import { getMeasurementTypeDisplay } from '../../utils/gameLogic';
import audioFileService from '../../services/audioFileService';

interface HomePageProps {
  onStartGame: (difficulty: 'easy' | 'medium' | 'hard' | 'mixed', gameMode: GameMode, measurementType?: 'length' | 'weight' | 'volume', categories?: string[]) => Promise<void>;
  gameState: any;
  profile?: UserProfile | null;
  onLeaderboardClick?: () => void;
}

const HomePage: React.FC<HomePageProps> = ({ onStartGame, profile, onLeaderboardClick }) => {
  const [selectedDifficulty, setSelectedDifficulty] = useState<'easy' | 'medium' | 'hard' | 'mixed'>('medium');
  const [showMeasurementSelector, setShowMeasurementSelector] = useState(false);
  const [showCategorySelector, setShowCategorySelector] = useState(false);
  const [pendingGameMode, setPendingGameMode] = useState<GameMode | null>(null);
  const [selectedMeasurementType, setSelectedMeasurementType] = useState<'length' | 'weight' | 'volume'>('length');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const handleGameStart = async (gameMode: GameMode) => {
    audioFileService.play('click');
    
    if (gameMode === 'measurement_focus') {
      // Show measurement type selector
      setPendingGameMode(gameMode);
      setShowMeasurementSelector(true);
    } else {
      // Start game directly
      await onStartGame(selectedDifficulty, gameMode, undefined, selectedCategories.length > 0 ? selectedCategories : undefined);
    }
  };

  const handleMeasurementTypeSelect = async (measurementType: 'length' | 'weight' | 'volume') => {
    setSelectedMeasurementType(measurementType);
    setShowMeasurementSelector(false);
    if (pendingGameMode) {
      await onStartGame(selectedDifficulty, pendingGameMode, measurementType, selectedCategories.length > 0 ? selectedCategories : undefined);
      setPendingGameMode(null);
    }
  };

  const handleLeaderboardClick = () => {
    audioFileService.play('click');
    onLeaderboardClick?.();
  };

  return (
    <div className="max-w-6xl mx-auto px-4">
      {/* Compact Header with Controls */}
      <motion.div 
        className="mb-6"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Title and Stats Row */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4">
          <div className="text-center lg:text-left mb-3 lg:mb-0">
            <h1 className="text-2xl md:text-3xl font-display font-bold text-banana-800 mb-1">
              {profile ? `Hey ${profile.username}! 🎮` : 'How Many Bananas? 🍌'}
            </h1>
            <p className="text-banana-700 text-sm">
              Test your intuition about size, weight, and volume!
            </p>
          </div>

          {/* Stats and Leaderboard */}
          <div className="flex flex-col sm:flex-row items-center gap-4">
            {profile && profile.stats.totalGames > 0 && (
              <div className="flex space-x-4 text-xs">
                <div className="text-center">
                  <div className="font-bold text-orange-600">{profile.stats.totalGames}</div>
                  <div className="text-gray-600">Games</div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-green-600">{profile.stats.bestScore}</div>
                  <div className="text-gray-600">Best</div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-blue-600">{profile.achievements.length}</div>
                  <div className="text-gray-600">Achievements</div>
                </div>
              </div>
            )}

            {onLeaderboardClick && (
              <button
                onClick={handleLeaderboardClick}
                className="btn-secondary px-4 py-1.5 text-xs"
              >
                🏆 Leaderboard
              </button>
            )}
          </div>
        </div>

        {/* Controls Row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-4 p-4 bg-banana-50 rounded-lg">
          {/* Difficulty Selector */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-banana-600 font-medium">Difficulty:</span>
            <div className="flex bg-white rounded-lg border border-banana-300 overflow-hidden shadow-sm">
              {(['easy', 'medium', 'hard', 'mixed'] as const).map((difficulty) => (
                <button
                  key={difficulty}
                  className={`px-2 py-1 text-xs font-medium transition-all ${
                    selectedDifficulty === difficulty
                      ? 'bg-banana-500 text-white'
                      : 'bg-white text-banana-700 hover:bg-banana-50'
                  }`}
                  onClick={() => {
                    setSelectedDifficulty(difficulty);
                    audioFileService.play('click');
                  }}
                  title={`${difficulty.charAt(0).toUpperCase() + difficulty.slice(1)} difficulty`}
                >
                  <div className="flex items-center gap-1">
                    <span className="text-xs">
                      {difficulty === 'easy' && '🟢'}
                      {difficulty === 'medium' && '🟡'}
                      {difficulty === 'hard' && '🔴'}
                      {difficulty === 'mixed' && '🔀'}
                    </span>
                    <span className="capitalize hidden sm:inline">{difficulty}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Category Selector */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-banana-600 font-medium">Categories:</span>
            <button
              onClick={() => setShowCategorySelector(true)}
              className="btn-secondary px-3 py-1 text-xs"
            >
              🏷️ {selectedCategories.length > 0 ? `${selectedCategories.length} Selected` : 'All'}
            </button>
          </div>
        </div>

        {/* Selected Categories */}
        {selectedCategories.length > 0 && (
          <div className="flex flex-wrap justify-center gap-1 mt-2">
            {selectedCategories.map(category => {
              const categoryInfo = getCategoryInfo()[category as keyof ReturnType<typeof getCategoryInfo>];
              return (
                <span key={category} className="inline-flex items-center gap-1 px-2 py-0.5 bg-banana-100 text-banana-800 rounded-full text-xs">
                  {categoryInfo?.emoji} {categoryInfo?.name}
                  <button
                    onClick={() => setSelectedCategories(prev => prev.filter(c => c !== category))}
                    className="ml-1 text-banana-600 hover:text-banana-800"
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        )}
      </motion.div>

      {/* Game Modes Grid */}
      <motion.div 
        className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        {gameModeConfigs.map((config, index) => (
          <motion.div
            key={config.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 * index }}
            className="card hover:shadow-lg transition-shadow group h-full"
          >
            <div className="text-center flex flex-col h-full p-4">
              <div className="text-3xl mb-2">{config.icon}</div>
              <h3 className="text-lg font-bold text-banana-800 mb-1">{config.name}</h3>
              <p className="text-banana-600 text-xs mb-3 flex-grow leading-relaxed">{config.description}</p>
              
              <button
                onClick={() => handleGameStart(config.id)}
                className="btn-primary px-6 py-2.5 text-sm w-full mt-auto"
              >
                Play Now
              </button>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Measurement Type Selector Modal */}
      {showMeasurementSelector && (
        <motion.div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setShowMeasurementSelector(false)}
        >
          <motion.div 
            className="bg-white rounded-lg p-8 max-w-md mx-4 shadow-2xl"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-2xl font-bold text-banana-800 mb-2 text-center">Choose Measurement Type</h3>
            <p className="text-banana-600 text-center mb-6">Select what you'd like to focus on:</p>
            
            <div className="space-y-3">
              {(['length', 'weight', 'volume'] as const).map((type) => {
                const display = getMeasurementTypeDisplay(type);
                return (
                  <motion.button
                    key={type}
                    className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                      selectedMeasurementType === type
                        ? 'border-banana-500 bg-banana-50'
                        : 'border-banana-200 hover:border-banana-300 bg-white'
                    }`}
                    onClick={() => handleMeasurementTypeSelect(type)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{display.icon}</span>
                      <div>
                        <div className="font-semibold text-banana-800">{display.name}</div>
                        <div className="text-sm text-banana-600">
                          {type === 'length' && 'Compare sizes and dimensions'}
                          {type === 'weight' && 'Compare how heavy things are'}
                          {type === 'volume' && 'Compare how much space things take up'}
                        </div>
                      </div>
                    </div>
                  </motion.button>
                );
              })}
            </div>
            
            <div className="flex justify-center mt-6">
              <button
                className="btn-secondary px-6 py-2"
                onClick={() => setShowMeasurementSelector(false)}
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Category Selector Modal */}
      {showCategorySelector && (
        <motion.div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setShowCategorySelector(false)}
        >
          <motion.div 
            className="bg-white rounded-lg p-6 max-w-2xl mx-4 shadow-2xl max-h-[80vh] overflow-y-auto"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-2xl font-bold text-banana-800 mb-2 text-center">Choose Categories</h3>
            <p className="text-banana-600 text-center mb-6">Select categories to focus on specific types of objects:</p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
              {Object.entries(getCategoryInfo()).map(([key, info]) => {
                const isSelected = selectedCategories.includes(key);
                return (
                  <motion.button
                    key={key}
                    className={`p-3 rounded-lg border-2 transition-all text-left ${
                      isSelected
                        ? 'border-banana-500 bg-banana-50'
                        : 'border-banana-200 hover:border-banana-300 bg-white'
                    }`}
                    onClick={() => {
                      if (isSelected) {
                        setSelectedCategories(prev => prev.filter(c => c !== key));
                      } else {
                        setSelectedCategories(prev => [...prev, key]);
                      }
                    }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{info.emoji}</span>
                      <div>
                        <div className="font-semibold text-banana-800 text-sm">{info.name}</div>
                        <div className="text-xs text-banana-600">{info.description}</div>
                      </div>
                    </div>
                  </motion.button>
                );
              })}
            </div>
            
            <div className="flex justify-center gap-3">
              <button
                className="btn-secondary px-4 py-2"
                onClick={() => {
                  setSelectedCategories([]);
                  setShowCategorySelector(false);
                }}
              >
                Clear All
              </button>
              <button
                className="btn-primary px-4 py-2"
                onClick={() => setShowCategorySelector(false)}
              >
                Done ({selectedCategories.length} selected)
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* How to Play Section */}
      <motion.div 
        className="bg-banana-50 rounded-lg p-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        <h3 className="text-lg font-bold text-banana-800 mb-3 text-center">How to Play</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-center mb-3">
          <div className="flex items-center justify-center gap-2 sm:flex-col">
            <div className="text-2xl">📏</div>
            <div>
              <h4 className="font-bold text-banana-800 text-xs">Length</h4>
              <p className="text-banana-700 text-xs">"How many bananas long?"</p>
            </div>
          </div>
          <div className="flex items-center justify-center gap-2 sm:flex-col">
            <div className="text-2xl">⚖️</div>
            <div>
              <h4 className="font-bold text-banana-800 text-xs">Weight</h4>
              <p className="text-banana-700 text-xs">"How many apples heavy?"</p>
            </div>
          </div>
          <div className="flex items-center justify-center gap-2 sm:flex-col">
            <div className="text-2xl">📦</div>
            <div>
              <h4 className="font-bold text-banana-800 text-xs">Volume</h4>
              <p className="text-banana-700 text-xs">"How many tennis balls fit?"</p>
            </div>
          </div>
        </div>
        <div className="text-center">
          <p className="text-banana-700 text-xs">
            💡 The closer your guess, the more points you earn!
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default HomePage; 